/**
 * PRE-FOLLOWUP-AUTO-RETRY-SWEEPER (PA-1) — client-side sweeper that
 * fires deferred provider retries when the user lands on the Site
 * Evidence tab.
 *
 * Background. PRE-3 (row 0.99251) stamps `nextRetryAt` on every SED
 * section that returned attempted-empty / http-error / timeout /
 * rate-limited via `nextRetryFromBackoff`. The PRE pack deferred the
 * eventual scheduler — described as a Cloud Run cron job in the spec
 * — and the live admin Provider Posture surface (PP-1, row 0.99267)
 * still ships the per-row retry button as a disabled
 * "Scheduler not wired" stub. PA-1 is the first concrete closer of
 * `nextRetryAt`: a tab-mount sweeper that reads each section's
 * `attemptResult` / `nextRetryAt` and fires the existing
 * `refreshSedSections` store action for any section whose backoff
 * window has expired.
 *
 * Pure (no I/O, no React). The sweeper itself lives in
 * `SiteEvidenceTab` (SiteLandingPage.tsx) — this module exposes the
 * selector + a cost-envelope helper so the orchestration is testable
 * without React, jsdom, or a fake store.
 *
 * Cost envelope (hard rules from the PA-1 brief):
 *   - Max 1 retry per section per session (tracked by the caller via
 *     a Set<SedSectionId>; this helper accepts the set and filters
 *     against it).
 *   - Rate-limit additionally on `nextRetryAt` — only sections where
 *     `nextRetryAt` is set AND parses to a time <= `now` are eligible.
 *     Sections without `nextRetryAt` (legacy verified rows, brand-new
 *     empty rows whose backoff window hasn't elapsed) are skipped.
 *   - `attemptResult === 'empty'` (and the other PRE failure modes) is
 *     the entrance gate — a section with `attemptResult: 'success'`
 *     never sweeps even if it somehow carries a `nextRetryAt`.
 *
 * Composes with:
 *   - `nextRetryFromBackoff` (PRE pack) — pure function that stamps the
 *     `nextRetryAt` this sweeper consumes.
 *   - `refreshSedSections` (store) — section-scoped re-fetch the
 *     sweeper invokes. Already shipped; no change here.
 *   - PP-1 disabled "Scheduler not wired" stub — re-labelled at the
 *     same time so the admin sees the sweeper is live.
 */

import type {
  SiteEvidenceDocument,
  EvidenceSection,
} from '../types/siteEvidence';
import { SED_SECTION_IDS, type SedSectionId } from '../types/siteEvidence';
import { describeUnavailableSection } from './evidenceUnavailableReason';

/**
 * Raw provider text for a missing or rejected key or a broken integration.
 * `describeUnavailableSection` classifies the producer's `emptyReason`; this
 * also catches older or unclassified sections where only the provider's own
 * message survives in `source` (e.g. "AirNow API key not configured — …").
 */
const NEEDS_ADMIN_CONFIG_TEXT = /API key (is )?not configured|API key was rejected|integration is broken/i;

/**
 * Attempt-result values that warrant an auto-retry. Mirrors the PRE
 * pack's backoff table — `nextRetryFromBackoff` only returns a non-null
 * ISO timestamp for these four states, so the additional filter here
 * is defence-in-depth: a stale `nextRetryAt` paired with `'success'` or
 * `'not-attempted'` is treated as "do not sweep" (the value would only
 * appear via a producer bug; surfacing the bug as a quiet skip is
 * safer than churning the upstream API).
 */
const RETRYABLE_ATTEMPT_RESULTS: ReadonlySet<NonNullable<EvidenceSection<unknown>['attemptResult']>> =
  new Set(['empty', 'http-error', 'timeout', 'rate-limited']);

/**
 * Sections that have no live provider behind them. `refreshSedSections`
 * silently drops these via `providersForSections` (SED_SECTION_PROVIDERS
 * → empty list), so sweeping them is a no-op. We filter here too so the
 * caller's "max 1 per session" budget isn't burned on a section that
 * could never actually retry.
 *
 * Kept inline rather than imported from `sedStaleness.ts` to avoid the
 * cyclic-ish dependency (sedStaleness reads EvidenceSection types from
 * here transitively); the list is short and tracked by tests.
 */
const NON_REFRESHABLE_SECTIONS: ReadonlySet<SedSectionId> = new Set<SedSectionId>([
  'parcel',
  'siteProfile',
  'adamsDocuments',
  'onSiteInfrastructure',
]);

/**
 * Parameters for `selectRetryableSections`.
 */
export interface SweepInput {
  readonly sed: SiteEvidenceDocument | null | undefined;
  /** Wall-clock now, in ms. Injectable for tests. */
  readonly now: number;
  /** Section ids already retried in this session (one-shot guard). */
  readonly alreadyRetried: ReadonlySet<SedSectionId>;
}

/**
 * One row per section the sweeper will request a retry for. The caller
 * (the SiteEvidenceTab useEffect) reads these and calls
 * `refreshSedSections(siteId, [sectionId])` per row, then stamps the
 * id into its session-Set so a second mount within the same session
 * is a no-op.
 *
 * `attemptedProvider` is informational — the store action reads its
 * provider list off `SED_SECTION_PROVIDERS`, not off the section
 * payload, so a missing/unknown provider string is harmless. Included
 * for audit-log + test introspection.
 */
export interface RetryableSection {
  readonly sectionId: SedSectionId;
  readonly attemptedProvider: string | undefined;
  readonly attemptResult: NonNullable<EvidenceSection<unknown>['attemptResult']>;
  readonly nextRetryAt: string;
}

/**
 * Pure selector. Walks every SED section, picks the ones whose
 * `attemptResult` is retryable AND whose `nextRetryAt` parses to a
 * time <= `now`, AND that the caller hasn't already swept this
 * session.
 *
 * Determinism: section order matches `SED_SECTION_IDS` so the caller
 * (and tests) can rely on a stable iteration order. The output is
 * deduplicated by section id by construction (each id appears at most
 * once in `SED_SECTION_IDS`).
 */
export function selectRetryableSections(input: SweepInput): RetryableSection[] {
  const { sed, now, alreadyRetried } = input;
  if (!sed) return [];

  const out: RetryableSection[] = [];
  for (const id of SED_SECTION_IDS) {
    if (NON_REFRESHABLE_SECTIONS.has(id)) continue;
    if (alreadyRetried.has(id)) continue;

    // `externalLiterature` has a different shape (no EvidenceSection
    // wrapper); the LL pack stores attempt state via the orchestrator
    // and doesn't expose `attemptResult` / `nextRetryAt` today, so
    // skipping here matches the producer's stance.
    if (id === 'externalLiterature') continue;

    // Every other section id maps onto an `EvidenceSection<T>` field
    // on the SED. Read defensively — legacy SEDs may not even carry
    // the field.
    const slot = (sed as unknown as Record<string, EvidenceSection<unknown> | undefined>)[id];
    if (!slot || typeof slot !== 'object') continue;
    // The selected study area's shape is unsupported by the provider: permanent until the selection changes.
    if (`${slot.emptyReason ?? ''} ${slot.source ?? ''}`.includes('unsupported-study-area-geometry')) continue;

    const attemptResult = slot.attemptResult;
    if (!attemptResult || !RETRYABLE_ATTEMPT_RESULTS.has(attemptResult)) continue;

    // A missing or rejected key, or a broken integration, fails the same way
    // on every retry — the producer collapses those modes into 'http-error',
    // so only the reason text tells them apart. Sweeping them spends a paid
    // top-up per visit and changes nothing.
    if (describeUnavailableSection(slot).kind === 'needs-admin-config') continue;
    if (NEEDS_ADMIN_CONFIG_TEXT.test(slot.emptyReason ?? slot.source ?? '')) continue;

    const nextRetryAtIso = slot.nextRetryAt;
    if (typeof nextRetryAtIso !== 'string' || nextRetryAtIso.length === 0) continue;
    const nextRetryAtMs = Date.parse(nextRetryAtIso);
    if (!Number.isFinite(nextRetryAtMs)) continue;
    if (nextRetryAtMs > now) continue;

    out.push({
      sectionId: id,
      attemptedProvider: slot.attemptedProvider,
      attemptResult,
      nextRetryAt: nextRetryAtIso,
    });
  }

  return out;
}

/**
 * Per-tab-mount cost envelope. The sweeper budgets across sections
 * within a single tab visit so a SED with 8 attempted-empty sections
 * doesn't fan out 8 simultaneous `topUpSiteSed` calls.
 *
 * Today's cap matches the PRE pack's R2 mitigation: backoff is
 * per-status (24h for legitimate empty, etc.), so the realistic
 * upper bound of "sections eligible for retry at any one moment" is
 * already low. A cap of 3 per visit keeps the worst-case provider
 * fan-out bounded without starving sections that haven't been swept
 * in days.
 */
export const MAX_RETRIES_PER_VISIT = 3;

/**
 * Apply the per-visit fan-out cap. Pure — returns the prefix of the
 * input list bounded by `MAX_RETRIES_PER_VISIT`. Kept separate from
 * `selectRetryableSections` so tests can pin the selector independent
 * of the cap.
 */
export function applyPerVisitCap(rows: ReadonlyArray<RetryableSection>): RetryableSection[] {
  if (rows.length <= MAX_RETRIES_PER_VISIT) return [...rows];
  return rows.slice(0, MAX_RETRIES_PER_VISIT);
}
